import { z } from 'zod';

/**
 * Lo que sube de la caseta a la nube: filas que cambiaron.
 *
 * ## Por qué esto sí necesita cola y el estado no
 *
 * Un acceso es un **evento**: ocurrió una vez, y si el envío se pierde no hay
 * forma de reconstruirlo. El estado de un aparato es una foto —si se pierde, la
 * siguiente corrige—. Por eso el estado va sin acuses ni reintentos y esto va
 * con una bandeja: cada fila se marca al cambiar y la marca no se cierra hasta
 * que la nube acusa.
 *
 * ## Por qué el orden es el de la bandeja
 *
 * Porque el orden cronológico **es** el orden causal: una invitación se crea
 * antes que el acceso que la referencia, y una foto después del acceso al que
 * pertenece. Aplicar las marcas por su número de bandeja hace que los padres
 * lleguen antes que los hijos sin que nadie tenga que ordenar nada a mano.
 *
 * Si en vez de eso se mandara «todo lo que cambió desde tal fecha», bastaría un
 * reloj atrasado para saltarse un tramo entero sin que nadie lo notara.
 */

/** Cuántas filas por envío. Un lote grande tarda; uno pequeño hace más viajes. */
export const LOTE_MAXIMO = 200;

/** Cada cuánto vacía la caseta su bandeja. */
export const SINCRONIZAR_INTERVALO_MS = 30_000;

/**
 * Las tablas que pueden viajar, y **en este orden**.
 *
 * Es una lista blanca y no una comprobación en el emisor: el receptor no puede
 * confiar en que quien manda esté sano, ni aunque su firma sea buena. Una
 * caseta comprometida firmaría igual de bien.
 *
 * El orden importa cuando hay que sembrar de cero, pero no en el día a día: ahí
 * manda el número de bandeja.
 */
export const TABLAS_QUE_SUBEN = [
  'Turno',
  'Tarjeton',
  'Invitacion',
  'Acceso',
  'Foto',
  'LecturaTag',
] as const;

export type TablaQueSube = (typeof TABLAS_QUE_SUBEN)[number];

export const cambioSchema = z.object({
  tabla: z.enum(TABLAS_QUE_SUBEN),
  filaId: z.string().min(1).max(64),
  operacion: z.enum(['INSERT', 'UPDATE', 'DELETE']),

  /**
   * La fila entera tal como está **ahora**, o nulo si se borró.
   *
   * Se manda el estado actual y no el de cuando se marcó: si la fila cambió
   * tres veces antes de subir, sube una vez y ya correcta. Eso es también lo
   * que hace el reenvío inofensivo — aplicar dos veces el mismo estado da el
   * mismo resultado.
   */
  fila: z.record(z.unknown()).nullable(),
});

export type Cambio = z.infer<typeof cambioSchema>;

export const loteSchema = z.object({
  condominio: z.string().min(1).max(60),
  /** El número de bandeja más alto del lote: lo que la nube acusa. */
  hasta: z.string().min(1).max(32),
  cambios: z.array(cambioSchema).max(LOTE_MAXIMO),
});

export type Lote = z.infer<typeof loteSchema>;

export interface LoteAceptado {
  /** Cuántas filas se escribieron de verdad. */
  aplicados: number;
  /** Hasta qué número de bandeja se puede dar por subido. */
  hasta: string;
}

// ── El otro sentido: lo que baja de la nube ──────────────────

/**
 * Las tablas que la **nube** manda hacia abajo, en orden de dependencia.
 *
 * Es lo que la nube administra: el padrón y la topología. Un residente se da de
 * alta arriba y la reja tiene que enterarse, porque es la que decide si abre.
 *
 * `Usuario` baja también, y no es un detalle menor: `Residente` apunta a él, y
 * sin la persona no se puede escribir la residencia. Trae la contraseña, que es
 * lo que permite entrar en la caseta cuando no hay internet.
 */
export const TABLAS_QUE_BAJAN = [
  'Zona',
  'PuntoAcceso',
  'Carril',
  'TipoVisita',
  'Dispositivo',
  'Usuario',
  'Unidad',
  'Residente',
  'Vehiculo',
  'Tag',
  'TelefonoUnidad',
  'Autorizacion',
  'Invitacion',
  // El celular se da de alta contra la nube, pero quien manda el aviso de
  // «llegó tu visita» es la caseta, al liberar el ingreso. Sin bajar el token,
  // el residente no recibiría nunca nada — y sin ningún error a la vista.
  'DispositivoPush',
  /**
   * La cara de una persona, **con su foto dentro de la fila**.
   *
   * Baja porque se enrola arriba y quien reconoce está abajo. La foto viaja en
   * la columna `foto` justamente para esto: mientras vivió solo como archivo en
   * disco, enrolar desde la nube era imposible —la sincronización mueve filas,
   * no archivos— y cada caseta acababa con su propio juego de caras.
   *
   * Va después de `Residente` y `Autorizacion` porque apunta a una de las dos.
   *
   * **`EnrolamientoFacial` no baja, y es a propósito.** Esa tabla dice qué
   * terminal tiene ya la cara metida, y eso lo sabe la caseta, no la nube: lo
   * escribe el propio aparato al aplicarla. Si bajara, la nube se lo pisaría
   * con un «pendiente» eterno cada vez y el terminal volvería a enrolar a todo
   * el mundo en cada sincronización. La caseta arma su propia cola a partir de
   * los rostros que le llegan.
   */
  'RostroFacial',
] as const;

export type TablaQueBaja = (typeof TABLAS_QUE_BAJAN)[number];

/**
 * Lo que la caseta pregunta: «¿qué ha cambiado desde este punto?».
 *
 * **La caseta pregunta; la nube no empuja.** Para empujar, la nube tendría que
 * poder alcanzar el servidor de la reja, y eso significa abrir un puerto hacia
 * dentro del fraccionamiento. Una reja alcanzable desde internet es una reja
 * que alguien puede alcanzar desde internet; la inmediatez no vale eso.
 */
export const pedirCambiosSchema = z.object({
  condominio: z.string().min(1).max(60),
  /** El último número de bandeja ya aplicado. `0` la primera vez. */
  desde: z.string().min(1).max(32),
});

export type PedirCambios = z.infer<typeof pedirCambiosSchema>;

/**
 * Cualquier tabla que viaje, en cualquiera de los dos sentidos.
 *
 * `Cambio` lleva `z.enum(TABLAS_QUE_SUBEN)` y **así tiene que quedarse**: es la
 * validación de lo que entra por la ruta que recibe lotes de las casetas, y es
 * lo que impide que una caseta comprometida pretenda escribir en el padrón.
 *
 * Pero el mismo tipo se usa para describir lo que **baja**, y ahí viajan las
 * otras: `Usuario`, `Unidad`, `RostroFacial`. La bajada no valida con Zod —es
 * la respuesta de la nube, no un cuerpo de fuera— así que el tipo no estorbaba
 * y se tapaba con un `as` en `cambiosPara()`. Lo que no estorba es que mienta;
 * lo que estorba es creerle: con el tipo estrecho, preguntar por una tabla que
 * solo baja es un error de compilación con el mensaje al revés —«estas dos
 * cosas no se solapan»— cuando la que no se solapa es la declaración.
 */
export type TablaEnViaje = TablaQueSube | TablaQueBaja;

/** Un cambio de los que viajan, sin estrechar la tabla a un solo sentido. */
export interface CambioEnViaje extends Omit<Cambio, 'tabla'> {
  tabla: TablaEnViaje;
}

export interface CambiosParaLaCaseta {
  cambios: CambioEnViaje[];
  /**
   * Hasta dónde llega esta respuesta. La caseta lo guarda **junto con los
   * datos, en la misma transacción**: si se corta a media aplicación se
   * reintenta el mismo tramo en vez de saltárselo.
   */
  hasta: string;
  /** Si quedan más. La caseta vuelve a preguntar en vez de esperar la vuelta. */
  hayMas: boolean;
}
